import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { type Permission } from '@aid/shared';
import { useAuthStore } from '../stores/auth-store';
import { UserNotLinkedPage } from './UserNotLinkedPage';
import { LoadingScreen } from '../components/LoadingScreen';
import { usePermissions } from '../providers';
import { useHasPermission } from './useHasPermission';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: Permission;
}

export function ProtectedRoute({ children, permission }: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized } = useAuthStore();
  const location = useLocation();
  const { isLoading: isPermissionsLoading, unlinked } = usePermissions();
  const hasPermission = permission ? useHasPermission(permission) : true;

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated in Keycloak but not provisioned in aid: a normal state in a
  // realm shared with other apps. Checked before the permission redirect so the
  // user is not bounced through `/` and the no-home screen first. Derived from
  // the /api/me query, so the gate lifts by itself once an admin provisions the
  // account and the query revalidates — no page reload required.
  if (unlinked) {
    return <UserNotLinkedPage />;
  }

  // Wait for /api/me to resolve before evaluating route permissions.
  // Without this, a direct URL load evaluates an empty permission set and
  // wrongly redirects before the sidebar-driven SPA navigation can mask it.
  if (permission && isPermissionsLoading) {
    return <LoadingScreen />;
  }

  if (permission && !hasPermission) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
